/**
 * Donor session state.
 *
 * Deliberately contains NO Firebase import. Reading who you are is just a fetch
 * to /auth/me; only *signing in* needs the SDK, and that lives behind the lazy
 * /account route. Importing Firebase here would put 37 kB of auth machinery in
 * front of every visitor who ever looks at the landing page — which is exactly
 * the regression this codebase already had once.
 */
import { useCallback, useEffect, useState } from 'react';

export interface DonorUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  profileComplete: boolean;
}

let cached: DonorUser | null | undefined;
const listeners = new Set<(user: DonorUser | null) => void>();

/** Shared so the header and the account page cannot disagree about who is in. */
export function publishSession(user: DonorUser | null): void {
  cached = user;
  for (const notify of listeners) notify(user);
}

export function useDonorSession() {
  const [user, setUser] = useState<DonorUser | null>(cached ?? null);
  const [loading, setLoading] = useState(cached === undefined);

  useEffect(() => {
    listeners.add(setUser);
    return () => { listeners.delete(setUser); };
  }, []);

  useEffect(() => {
    if (cached !== undefined) return;
    const controller = new AbortController();
    fetch('/api/v1/auth/me', { credentials: 'same-origin', signal: controller.signal })
      .then((r) => r.json())
      .then((data: { user: DonorUser | null }) => {
        publishSession(data.user);
        setLoading(false);
      })
      .catch((cause) => {
        // An aborted request (StrictMode's double-invoked effect cleanup, or
        // a fast navigation away) is not "signed out" — it is this specific
        // fetch never finishing. Treating it as a real answer means the
        // *other*, still-in-flight effect instance's eventual real answer can
        // get raced and overwritten by this one's `publishSession(null)`, or
        // `loading` can flip to `false` before that real answer ever arrives.
        if (cause?.name === 'AbortError') return;
        publishSession(null);
        setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const signOut = useCallback(async () => {
    /**
     * The CSRF header is required, not optional.
     *
     * Without it the server returns 403 and — because the failure used to be
     * swallowed — the local state cleared while the session cookie survived, so
     * a refresh signed the person straight back in. On a shared monastery
     * office computer that is the failure that matters most, so this now
     * reports rather than hides it.
     */
    const csrf = document.cookie.match(/(?:^|;\s*)gompa_csrf=([^;]+)/)?.[1];
    const response = await fetch('/api/v1/auth/signout', {
      method: 'POST',
      credentials: 'same-origin',
      headers: csrf ? { 'x-gompa-csrf': decodeURIComponent(csrf) } : {},
    }).catch(() => null);

    if (!response?.ok) {
      throw new Error('Could not sign out. Reload the page and try again.');
    }

    // Firebase is signed out too, but only if it was ever loaded — a donor who
    // never signed in should not pull the SDK just to press this.
    const mod = await import('./firebase').catch(() => null);
    await mod?.signOutOfFirebase();
    publishSession(null);
  }, []);

  return { user, loading, signOut };
}
